const STORE_CHECKOUT_SCROLL_ID = 'store-checkout-id';
const PORTAL_MAIN_SECTION_ID = 'main-section';
const ORDER_PAGE_MOBILE_SELECTOR = '.order-page--mobile';
const STORE_CHECKOUT_STEPS_WRAPPER_SELECTOR =
  '[class*="store-checkout--"][class*="__steps-wrapper"], [class*="__steps-wrapper"]';
const MODAL_SCROLL_ALLOW_SELECTOR = '.cp-modal, .cp-modal *';

export const getOrderPageMobileScrollElement = (): HTMLElement | null => {
  const orderPage = document.querySelector(ORDER_PAGE_MOBILE_SELECTOR);
  return orderPage instanceof HTMLElement ? orderPage : null;
};

export interface IPageScrollSnapshot {
  scrollElement: HTMLElement | 'window';
  scrollTop: number;
}

interface ILockedElementState {
  element: HTMLElement;
  overflow: string;
  overflowY: string;
  touchAction: string;
  overscrollBehavior: string;
  paddingRight: string;
  scrollTop: number;
}

let modalScrollLockCount = 0;
let lockedScrollSnapshot: IPageScrollSnapshot | null = null;
let lockedElementStates: ILockedElementState[] = [];
let lockedBodyOverflow = '';
let backgroundScrollBlockAttached = false;

const isOverflowScrollable = (overflowY: string): boolean =>
  overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';

/** True only when the element can actually receive scroll (not just taller than its box). */
const isScrollableElement = (element: HTMLElement): boolean => {
  if (element.scrollHeight <= element.clientHeight + 1) {
    return false;
  }

  const { overflowY } = window.getComputedStyle(element);
  return isOverflowScrollable(overflowY);
};

const getStoreCheckoutStepsWrapper = (): HTMLElement | null => {
  const storeCheckout = document.getElementById(STORE_CHECKOUT_SCROLL_ID);
  if (!storeCheckout) {
    return null;
  }

  const stepsWrapper = storeCheckout.querySelector(STORE_CHECKOUT_STEPS_WRAPPER_SELECTOR);
  return stepsWrapper instanceof HTMLElement ? stepsWrapper : null;
};

const getScrollLockCandidates = (): HTMLElement[] => {
  const storeCheckout = document.getElementById(STORE_CHECKOUT_SCROLL_ID);
  const mainSection = document.getElementById(PORTAL_MAIN_SECTION_ID);
  const orderPageMobile = getOrderPageMobileScrollElement();
  const stepsWrapper = getStoreCheckoutStepsWrapper();

  return [stepsWrapper, storeCheckout, mainSection, orderPageMobile].filter(
    (element): element is HTMLElement => element instanceof HTMLElement
  );
};

const isEventInsideModal = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(MODAL_SCROLL_ALLOW_SELECTOR));
};

const preventBackgroundScrollEvent = (event: Event): void => {
  if (isEventInsideModal(event.target)) {
    return;
  }

  event.preventDefault();
};

const attachBackgroundScrollBlock = (): void => {
  if (backgroundScrollBlockAttached) {
    return;
  }

  document.addEventListener('touchmove', preventBackgroundScrollEvent, { passive: false });
  document.addEventListener('wheel', preventBackgroundScrollEvent, { passive: false });
  backgroundScrollBlockAttached = true;
};

const detachBackgroundScrollBlock = (): void => {
  if (!backgroundScrollBlockAttached) {
    return;
  }

  document.removeEventListener('touchmove', preventBackgroundScrollEvent);
  document.removeEventListener('wheel', preventBackgroundScrollEvent);
  backgroundScrollBlockAttached = false;
};

export const capturePageScrollSnapshot = (): IPageScrollSnapshot => {
  const candidates = getScrollLockCandidates();

  const scrolledCandidate = candidates.find((element) => element.scrollTop > 0);
  if (scrolledCandidate) {
    return { scrollElement: scrolledCandidate, scrollTop: scrolledCandidate.scrollTop };
  }

  const scrollableCandidate = candidates.find(isScrollableElement);
  if (scrollableCandidate) {
    return { scrollElement: scrollableCandidate, scrollTop: scrollableCandidate.scrollTop };
  }

  if (window.scrollY > 0) {
    return { scrollElement: 'window', scrollTop: window.scrollY };
  }

  const mainSection = document.getElementById(PORTAL_MAIN_SECTION_ID);
  if (mainSection instanceof HTMLElement) {
    return { scrollElement: mainSection, scrollTop: mainSection.scrollTop };
  }

  const storeCheckout = document.getElementById(STORE_CHECKOUT_SCROLL_ID);
  if (storeCheckout instanceof HTMLElement) {
    return { scrollElement: storeCheckout, scrollTop: storeCheckout.scrollTop };
  }

  const orderPageMobile = getOrderPageMobileScrollElement();
  if (orderPageMobile instanceof HTMLElement) {
    return { scrollElement: orderPageMobile, scrollTop: orderPageMobile.scrollTop };
  }

  return { scrollElement: 'window', scrollTop: window.scrollY };
};

export const restorePageScrollSnapshot = (snapshot: IPageScrollSnapshot): void => {
  if (snapshot.scrollElement === 'window') {
    window.scrollTo(0, snapshot.scrollTop);
    return;
  }

  snapshot.scrollElement.scrollTop = snapshot.scrollTop;
};

/** @deprecated Prefer capturePageScrollSnapshot — kept for tests and callers. */
export const getPortalMainScrollElement = (): HTMLElement | null => {
  const mainSection = document.getElementById(PORTAL_MAIN_SECTION_ID);
  return mainSection instanceof HTMLElement ? mainSection : null;
};

const freezeScrollElement = (element: HTMLElement): void => {
  const scrollbarWidth = element.offsetWidth - element.clientWidth;

  lockedElementStates.push({
    element,
    overflow: element.style.overflow,
    overflowY: element.style.overflowY,
    touchAction: element.style.touchAction,
    overscrollBehavior: element.style.overscrollBehavior,
    paddingRight: element.style.paddingRight,
    scrollTop: element.scrollTop,
  });

  element.style.overflow = 'hidden';
  element.style.overflowY = 'hidden';
  element.style.touchAction = 'none';
  element.style.overscrollBehavior = 'none';
  if (scrollbarWidth > 0) {
    element.style.paddingRight = `${scrollbarWidth}px`;
  }
};

const unfreezeScrollElements = (): void => {
  lockedElementStates.forEach((state) => {
    state.element.style.overflow = state.overflow;
    state.element.style.overflowY = state.overflowY;
    state.element.style.touchAction = state.touchAction;
    state.element.style.overscrollBehavior = state.overscrollBehavior;
    state.element.style.paddingRight = state.paddingRight;
    state.element.scrollTop = state.scrollTop;
  });
  lockedElementStates = [];
};

const lockBodyScroll = (scrollTopForWindow: number): void => {
  lockedBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  // Classic body pin only when the window itself was scrolling.
  // Nested scrollers (e.g. #main-section) are frozen separately to avoid a layout jump.
  if (lockedScrollSnapshot?.scrollElement === 'window') {
    document.body.classList.add('cp-modal-open');
    document.body.style.top = `-${scrollTopForWindow}px`;
    document.body.style.width = '100%';
  }
};

const unlockBodyScroll = (scrollTopForWindow: number): void => {
  const wasWindowLock = lockedScrollSnapshot?.scrollElement === 'window';

  document.body.classList.remove('cp-modal-open');
  document.body.style.overflow = lockedBodyOverflow;
  lockedBodyOverflow = '';
  document.body.style.top = '';
  document.body.style.width = '';

  if (wasWindowLock) {
    window.scrollTo(0, scrollTopForWindow);
  }
};

export const lockPageScrollForModal = (): void => {
  const snapshot = capturePageScrollSnapshot();
  lockedScrollSnapshot = snapshot;

  modalScrollLockCount += 1;
  if (modalScrollLockCount > 1) {
    return;
  }

  // Freeze every known page scroller (not only the "primary" one).
  const candidates = getScrollLockCandidates();
  const uniqueCandidates = Array.from(new Set(candidates));
  uniqueCandidates.forEach(freezeScrollElement);

  lockBodyScroll(snapshot.scrollTop);
  attachBackgroundScrollBlock();
};

export const unlockPageScrollForModal = (): void => {
  const snapshot = lockedScrollSnapshot;

  modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);

  if (modalScrollLockCount > 0) {
    return;
  }

  if (!snapshot) {
    return;
  }

  detachBackgroundScrollBlock();
  unfreezeScrollElements();
  unlockBodyScroll(snapshot.scrollTop);
  restorePageScrollSnapshot(snapshot);

  lockedScrollSnapshot = null;
};
